import { z } from 'zod/v4';

import {
    animationFeature,
    animationV0Schema,
    compactColumnProps,
    createDefaultColumn,
    type DefaultColumn,
} from '@sites/entity-core';

import type { IgnoredField } from '../validator/forgiving-validator.type';
import {
    boxPatchShape,
    everyDevice,
    foldBoxPatch,
    mentions,
    patchEveryDevice,
    RENAMED_BOX_KEYS,
    scrollEffectShape,
    type BoxPatch,
    type PropsBag,
} from './props-patch';

/**
 * AI-facing schemas for the `create_column` / `update_column` tools, and the fold that lands them on
 * a v0 column's `props`.
 *
 * FLAT on purpose — see `props-patch.ts`. The entity is `@sites/entity-core`'s
 * `columnDefaultV0Schema`; this is the tool-facing projection of it, keeping the vocabulary the
 * legacy AI surface used (`columnWidth`, `verticalAlign`, `backgroundColor`, `paddingTop`, …) so
 * tool descriptions, the system prompt and the model's own priors all still line up.
 *
 * `apps/mcp-backend` folds a patch onto a full default column (create) or onto the stored column
 * (update); `apps/websites`' `create_column` operation folds the same patch onto the column the
 * editor's own factory just built. Everything the model omits keeps its existing/default value.
 */

/**
 * The AI's default column: entity-core's ordinary column, nothing added.
 *
 * A named factory rather than `createDefaultColumn()` at each call site, so "what does an
 * AI-created column start as" has one answer shared by `apps/mcp-backend`'s create route and
 * `apps/mcp-server`'s stub — the same reason `createBlockScaffold` lives in this package. It also
 * keeps entity-core out of mcp-server, which has never imported it.
 */
export function createAiDefaultColumn(): DefaultColumn {
    return createDefaultColumn();
}

/**
 * Everyday alignment words → the codes v0 stores. Legacy stored `'T' | 'M' | 'B'` and
 * `'L' | 'M' | 'R'`; v0 spells both out, so the single-letter forms are accepted as aliases (the
 * model has seen them in older prompts, and `'M'` is ambiguous between the two axes without them).
 */
const VERTICAL_ALIGN_ALIASES: Readonly<Record<string, 'top' | 'middle' | 'bottom'>> = {
    t: 'top',
    top: 'top',
    m: 'middle',
    middle: 'middle',
    center: 'middle',
    centre: 'middle',
    b: 'bottom',
    bottom: 'bottom',
};

const HORIZONTAL_ALIGN_ALIASES: Readonly<Record<string, 'left' | 'center' | 'right'>> = {
    l: 'left',
    left: 'left',
    start: 'left',
    m: 'center',
    c: 'center',
    center: 'center',
    centre: 'center',
    middle: 'center',
    r: 'right',
    right: 'right',
    end: 'right',
};

const alias = <T extends string>(map: Readonly<Record<string, T>>) =>
    z.preprocess(
        (raw) => {
            if (typeof raw !== 'string') {
                return raw;
            }

            return map[raw.trim().toLowerCase()] ?? raw;
        },
        z.enum(Object.values(map) as [T, ...T[]])
    );

/**
 * Keys a v0 column does not store — reasons handed to `validateForgiving` as `unknownKeyReasons`,
 * not fields in the shape. See the twin in `block.schema.ts` for what that bought and why the
 * `data`/`styles` buckets stay listed here even though the surface no longer accepts them.
 */
/**
 * Read off the feature's own schema rather than retyped beside the field — unlike the contour style
 * and the scroll effect, which live in `host-enums.ts` because `@sites/entity-core` stores those as
 * plain strings and cannot refuse a wrong one. Animation is the opposite case: the feature already
 * declares three real enums, so the only way to keep this surface in step with them is to ask.
 */
const ANIMATION_TYPES = animationV0Schema.shape.animationType.options;
const ANIMATION_POSITIONS = animationV0Schema.shape.animationPosition.options;
const ANIMATION_SPEEDS = animationV0Schema.shape.animationSpeed.options;

export const IGNORED_COLUMN_KEYS = {
    data: 'column settings are FLAT top-level arguments — send them directly, not wrapped in "data"',
    styles: 'column settings are FLAT top-level arguments — send them directly, not wrapped in "styles"',
    isIndependentBorders: 'derived from the four sides in v0 — set the per-side border keys instead',
    isIndependentCorners: 'derived from the four corners in v0 — set borderRadius instead',
    backgroundImage: 'a background image must be picked from the media library, which this tool cannot do',
    ...RENAMED_BOX_KEYS,
} as const satisfies Readonly<Record<string, string>>;

export const columnPatchShape = {
    ...boxPatchShape,

    /**
     * In the row's OWN unit — twelfths of the grid, or percent when the row runs flexible widths.
     * The fold keeps the stored unit rather than picking one: a `col` value written into a `%` row
     * (or the reverse) is a number read in a unit it was never written in, which is exactly the
     * drift legacy's two separate width fields produced.
     */
    columnWidth: z
        .number()
        .optional()
        .describe("Column width in the row's own unit — twelfths of the grid (1-12), or percent for a flexible row."),

    verticalAlign: alias(VERTICAL_ALIGN_ALIASES)
        .optional()
        .describe('How the column aligns its widgets vertically: "top" | "middle" | "bottom".'),
    horizontalAlign: alias(HORIZONTAL_ALIGN_ALIASES)
        .optional()
        .describe('How the column aligns its widgets horizontally: "left" | "center" | "right".'),

    syncWithDesktop: z
        .boolean()
        .optional()
        .describe('Write desktop style edits through to the tablet and mobile tabs.'),

    borderRadius: z.number().optional().describe('Corner rounding for all four corners, px.'),

    isShadow: z.boolean().optional().describe('Cast a box-shadow.'),
    shadowSettings: z
        .object({
            offsetX: z.number(),
            offsetY: z.number(),
            blur: z.number(),
            spread: z.number(),
            color: z.string(),
        })
        .partial()
        .optional()
        .describe('The shadow itself; omit it and a sane default shadow is used.'),

    /**
     * Declared as the stored group minus its `version`, rather than as flat `isAnimation` +
     * `animationSettings` in the style of the shadow above. The shadow's split mirrors a split the
     * ENTITY has (`shadow.isEnabled` beside `shadow.settings`); animation has none — `isActive` sits
     * in the same bag as the three settings — so inventing the boundary here would be a shape the
     * model has to learn for no reason. `animation: { … }` is also exactly what it already sends to
     * the text and image widgets, and exactly the key it was already trying on columns: it was an
     * accepted-but-ignored key until 2026-09-02 precisely because the model kept reaching for it.
     */
    animation: z
        .object({
            isActive: z.boolean().describe('Play the animation. The other three are not read while this is false.'),
            animationType: z
                .enum(ANIMATION_TYPES)
                .describe(
                    'What the column does as it arrives: "fade" fades in, "scale" grows from nothing, ' +
                        '"slide" only travels. NOTE that "slide" with animationPosition "center" travels ' +
                        'nowhere and changes nothing on the page — pair "slide" with a side.'
                ),
            animationPosition: z
                .enum(ANIMATION_POSITIONS)
                .describe(
                    'Where it travels FROM — "left" and "right" move it 100px sideways, "top" and ' +
                        '"bottom" 100px vertically, "center" not at all. Read for every type, not just ' +
                        '"slide": "fade" + "top" is a fade that also drifts down into place.'
                ),
            animationSpeed: z
                .enum(ANIMATION_SPEEDS)
                .describe('How long it takes: "slow" 0.8s, "medium" 0.4s, "fast" 0.2s.'),
        })
        .partial()
        .optional()
        .describe(
            'Entrance animation, played when the column scrolls into view. Partial — only the keys you ' +
                'send change, so { "isActive": true } turns on whatever is already configured. Animations ' +
                'live on the COLUMN and on individual widgets; a block has none, so "animate this block" ' +
                'means animating its columns.'
        ),

    ...scrollEffectShape,
} as const;

/**
 * The column's twin of the block's check — see `block.schema.ts` for why the invariant flipped and
 * why it still fails the build rather than a test.
 */
type DeclaredIgnoredColumnKey = Extract<keyof typeof IGNORED_COLUMN_KEYS, keyof typeof columnPatchShape>;

const _noIgnoredColumnKeyIsDeclared: DeclaredIgnoredColumnKey extends never ? true : DeclaredIgnoredColumnKey = true;

const columnPatchSchema = z.object(columnPatchShape);
export type ColumnPatch = BoxPatch & z.infer<typeof columnPatchSchema>;

export const createColumnArgsSchema = z.object({
    sectionId: z.string().describe('Id of the section to create the column in.'),
    index: z
        .number()
        .int()
        .min(0)
        .optional()
        .describe("Position within the section's column list (0-based). Omit to append at the end."),
    ...columnPatchShape,
});

export const updateColumnArgsSchema = z.object({
    columnId: z.string().describe('Id of the column to update.'),
    ...columnPatchShape,
});

/** The patch keys, i.e. everything a create/update call carries that isn't an id or a position. */
export const COLUMN_PATCH_KEYS: readonly string[] = Object.keys(columnPatchShape);

const SHADOW_KEYS = ['isShadow', 'shadowSettings'] as const;
const SCROLL_EFFECT_KEYS = ['isScrollEffect', 'scrollEffect'] as const;

const RADIUS_SEED = () => ({ value: { topLeft: 0, topRight: 0, bottomRight: 0, bottomLeft: 0 }, unit: 'px' as const });

/**
 * Folds a flat AI patch onto a v0 column's `props`, returning the new `props` plus whatever was
 * accepted-but-not-stored. Groups the patch doesn't mention keep their stored object.
 *
 * A zero column has no props at all and a header column only two, so every group is written by key
 * onto whatever the column already has — a patch naming a knob its subtype doesn't carry adds it and
 * the schema's own validation is what rejects the entity, rather than this silently inventing a
 * shape.
 */
export function foldColumnPatch(props: PropsBag, patch: ColumnPatch): { props: PropsBag } {
    const next = foldBoxPatch(props, patch);

    if (patch.columnWidth !== undefined) {
        const stored = (props['width'] ?? {}) as { unit?: 'col' | '%' };
        next['width'] = { value: patch.columnWidth, unit: stored.unit ?? 'col' };
    }

    if (patch.verticalAlign !== undefined) {
        next['verticalAlign'] = patch.verticalAlign;
    }

    if (patch.horizontalAlign !== undefined) {
        next['horizontalAlign'] = patch.horizontalAlign;
    }

    if (patch.syncWithDesktop !== undefined) {
        next['syncWithDesktop'] = patch.syncWithDesktop;
    }

    if (patch.borderRadius !== undefined) {
        const radius = { ...((props['borderRadius'] ?? {}) as PropsBag) };
        const corner = patch.borderRadius;

        radius['corners'] = patchEveryDevice(
            radius['corners'],
            (stored: { value: object; unit: string }) => ({
                ...stored,
                value: { topLeft: corner, topRight: corner, bottomRight: corner, bottomLeft: corner },
            }),
            RADIUS_SEED
        );
        next['borderRadius'] = radius;
    }

    if (mentions(patch, SHADOW_KEYS)) {
        const shadow = { ...((props['shadow'] ?? {}) as PropsBag) };

        if (patch.isShadow !== undefined) {
            shadow['isEnabled'] = patch.isShadow;
        }

        if (patch.shadowSettings !== undefined) {
            shadow['settings'] = { ...((shadow['settings'] ?? {}) as PropsBag), ...patch.shadowSettings };
        }

        next['shadow'] = shadow;
    }

    if (patch.animation !== undefined) {
        // Merged onto the stored group, never replacing it: the surface is partial, so
        // `{ isActive: true }` must turn the animation on without also resetting its type, direction
        // and speed to whatever this object happens not to carry.
        //
        // Seeded from the feature's own default when the column has none, and that is what carries
        // `version`. Writing the four settings without it would produce a group `migrateEntity`
        // cannot route — the field is self-versioned (`features/animation`), and its version is the
        // one key this surface must never author itself.
        const stored = props['animation'] as PropsBag | undefined;
        const base = stored ?? (animationFeature.createDefault() as unknown as PropsBag);

        next['animation'] = { ...base, ...patch.animation };
    }

    // Flat, not responsive: a column's scroll effect is one value for every breakpoint (the block's
    // is the responsive one, inside its background — see `scrollEffectFeature`).
    if (mentions(patch, SCROLL_EFFECT_KEYS)) {
        const scrollEffect = { ...((props['scrollEffect'] ?? {}) as PropsBag) };

        if (patch.isScrollEffect !== undefined) {
            scrollEffect['isEnabled'] = patch.isScrollEffect;
        }

        if (patch.scrollEffect !== undefined) {
            scrollEffect['value'] = patch.scrollEffect;
        }

        next['scrollEffect'] = scrollEffect;
    }

    return { props: compactColumnProps(next) };
}

/** True when the patch names at least one key the fold would act on — the "empty patch" guard. */
export function hasColumnPatch(patch: object): boolean {
    return mentions(patch, COLUMN_PATCH_KEYS);
}

export { everyDevice };
